import { PreparedItem, PreparedFolder } from '@/models/item.models';
import { useItemStore } from '@/store/itemStore';
import { Ref, ref } from 'vue';
import omit from 'lodash-es/omit';
import { prepareWorkItems } from '@/store/helpers';
import { unref } from 'vue';
import { FindItemsData, ItemType } from '@/api';

export function useFetchChildren(config: {
  query: Ref<FindItemsData['query']> | FindItemsData['query'];
  key?: string;
  parent?: Ref<PreparedItem | undefined>;
  preventFilteringPlannedFolders?: boolean;
  checkItemChildren?: boolean;
}) {
  const moreFoldersLoading = ref(false);
  const moreItemsLoading = ref(false);
  const childrenLoading = ref(false);
  const items = ref<Array<PreparedItem>>([]);
  const folders = ref<Array<PreparedFolder>>([]);
  const itemCursor = ref<string | null>(null);
  const folderCursor = ref<string | null>(null);

  async function fetchChildItems(overrideType?: ItemType, initialLoad = false) {
    const query = unref(config.query);
    const { limit, itemType, parentNumber } = query || {};
    if (initialLoad) {
      childrenLoading.value = true;
    } else if (overrideType === ItemType.FOLDER) {
      moreFoldersLoading.value = true;
    } else if (overrideType === ItemType.WORKITEM) {
      moreItemsLoading.value = true;
    } else {
      childrenLoading.value = true;
    }

    if (!query) {
      return;
    }

    const payload = (
      type: ItemType
    ): { query: FindItemsData['query']; key?: string } => {
      let hasNoBoard =
        type === ItemType.FOLDER
          ? query.hasNoBoard || query.isBacklog
          : query.hasNoBoard;
      if (config.preventFilteringPlannedFolders) {
        hasNoBoard = undefined;
      }
      return {
        query: {
          ...omit(query, ['isBacklog']),
          isBacklog: type === ItemType.WORKITEM ? query.isBacklog : undefined,
          hasNoBoard,
          limit: limit || 100,
          itemType: type,
          checkChildren:
            type === ItemType.WORKITEM
              ? config.checkItemChildren ?? query.checkChildren ?? undefined
              : query.checkChildren ?? undefined,

          cursor:
            (type === ItemType.FOLDER
              ? folderCursor.value
              : itemCursor.value) || undefined,
        },
        key: config.key,
      };
    };

    const shouldGetItems =
      overrideType !== ItemType.FOLDER &&
      parentNumber &&
      (!itemType || itemType === ItemType.WORKITEM);
    // TODO: this is a potential bug for when thr parent number is an item, this folder call will return a 404
    const shouldGetFolders =
      overrideType !== ItemType.WORKITEM &&
      (!itemType || itemType === ItemType.FOLDER);

    // NOTE: removed these next lines to fix a bug where the backlog panel on the Cycles page wasn't updating when planned items where toggled inside new folders
    // leaving in case we have to revert this
    // if (
    //   shouldGetItems &&
    //   config.parent?.value &&
    //   (config.parent?.value as PreparedFolder)?.itemCount < 1
    // ) {
    //   shouldGetItems = false;
    // }

    // if (
    //   shouldGetFolders &&
    //   config.parent?.value &&
    //   (config.parent.value as PreparedFolder).folderCount < 1
    // ) {
    //   shouldGetFolders = false;
    // }

    const itemStore = useItemStore();

    if (config.key) {
      // Make sure cache exists even if we don't make any API calls
      itemStore.addItems({
        items: [],
        keys: [config.key],
      });
    }

    const [itemResponse, folderResponse] = await Promise.allSettled([
      shouldGetItems
        ? itemStore.search(payload(ItemType.WORKITEM))
        : Promise.resolve(),
      shouldGetFolders
        ? itemStore.search(payload(ItemType.FOLDER))
        : Promise.resolve(),
    ]);
    if (
      itemResponse.status === 'fulfilled' &&
      shouldGetItems &&
      itemResponse.value
    ) {
      const responseItems = itemResponse.value.items;
      items.value = items.value.concat(responseItems);
      itemCursor.value = itemResponse.value.response?.meta?.cursor || null;
    }
    if (
      folderResponse.status === 'fulfilled' &&
      shouldGetFolders &&
      folderResponse.value
    ) {
      const responseFolders = folderResponse.value.items;
      folders.value = folders.value.concat(responseFolders as PreparedFolder[]);
      folderCursor.value = folderResponse.value.response?.meta?.cursor || null;
    }

    childrenLoading.value = false;
    moreFoldersLoading.value = false;
    moreItemsLoading.value = false;
  }

  async function fetchRootFolders() {
    const query = unref(config.query);
    let cursor: string | undefined;
    if (!query) return [];
    const payload = () => ({
      query: {
        ...omit(query, ['isBacklog', 'parentNumber']),
        hasNoBoard: query.hasNoBoard || query.isBacklog,
        hasNoParent: true,
        limit: 100,
        itemType: ItemType.FOLDER,
        cursor: cursor,
      },
    });

    const itemStore = useItemStore();

    const rootFolders: Array<PreparedFolder> = [];

    do {
      const { items } = await itemStore.search(payload());
      if (!items) return [];
      const preparedFolders = (await prepareWorkItems(
        items
      )) as Array<PreparedFolder>;
      rootFolders.push(...preparedFolders);
    } while (cursor);

    return rootFolders;
  }

  return {
    items,
    folders,
    moreFoldersLoading,
    moreItemsLoading,
    childrenLoading,
    fetchChildItems,
    itemCursor,
    folderCursor,
    fetchRootFolders,
  };
}
